import React, { useEffect, useState } from 'react';
import { app } from '../../firebase/firebaseConfig';
import { doc, getFirestore, onSnapshot } from 'firebase/firestore';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { ArrowLeft, Tv, Power, Info, Radio } from 'lucide-react';

// Live view of what the Hearth has learned about the connected TV over HDMI —
// identity (from EDID), CEC vendor/version/name, live power + active-source
// state, and whether this TV has ever forwarded a remote key. Reads the same
// owner-visible status doc the device writes (deviceManagement/{fam}/status/
// accountVisible.tv); read-only.
function TvInfoViewer({ device, onBack }) {
  // undefined = loading, null = no TV data yet, object = data
  const [tv, setTv] = useState(undefined);

  const familyID = device?.id;
  const deviceName = device?.familyName || familyID;

  useEffect(() => {
    if (!familyID) return;
    const db = getFirestore(app);
    const docRef = doc(db, 'deviceManagement', familyID, 'status', 'accountVisible');
    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        const data = snapshot.exists() ? snapshot.data() : null;
        setTv(data && data.tv ? data.tv : null);
      },
      (error) => console.error('TvInfoViewer: listener error:', error),
    );
    return () => unsubscribe();
  }, [familyID]);

  const dash = (v) =>
    v === undefined || v === null || v === '' ? '—' : String(v);

  const powerBadge = (status) => {
    const map = {
      ON: 'bg-green-100 text-green-800',
      STANDBY: 'bg-gray-200 text-gray-700',
      TO_ON: 'bg-blue-100 text-blue-800',
      TO_STANDBY: 'bg-amber-100 text-amber-800',
    };
    const cls = map[status] || 'bg-gray-100 text-muted-foreground';
    return (
      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
        {dash(status)}
      </span>
    );
  };

  const activeSourceText = () => {
    if (!tv) return '—';
    if (tv.isActiveSource === true) return 'Yes — the Hearth is on screen';
    if (tv.activeSourcePhys) return `No — another source is active (phys ${tv.activeSourcePhys})`;
    return 'No / unknown';
  };

  const sizeText = () => {
    if (!tv) return '—';
    const diag = tv.screenDiagonalIn ? `${tv.screenDiagonalIn}"` : null;
    const dims =
      tv.screenWidthCm && tv.screenHeightCm
        ? `${tv.screenWidthCm}×${tv.screenHeightCm} cm`
        : null;
    return [diag, dims].filter(Boolean).join('  ·  ') || '—';
  };

  const Row = ({ label, children }) => (
    <div className="flex justify-between gap-4 py-1.5 border-b last:border-b-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm text-right">{children}</span>
    </div>
  );

  const updatedText = tv?.lastInfoUpdateMs
    ? new Date(tv.lastInfoUpdateMs).toLocaleString()
    : null;

  return (
    <div className="mx-auto p-6 min-h-screen flex flex-col max-w-3xl">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="outline" onClick={onBack} className="flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back to Device Management
        </Button>
        <Tv className="w-8 h-8" />
        <div>
          <h1 className="text-3xl font-bold">TV Info</h1>
          <p className="text-sm text-muted-foreground">{deviceName}</p>
        </div>
      </div>

      <p className="text-sm text-muted-foreground mb-4">
        What the Hearth has learned about the connected TV over HDMI (CEC + EDID).
        Live — updates as the device observes changes.
        {updatedText ? ` Last update: ${updatedText}.` : ''}
      </p>

      {tv === undefined ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : tv === null ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            <Tv className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No TV info reported yet.</p>
            <p className="text-xs mt-1">
              The device hasn't written TV info (offline, or an older app version).
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Live state */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-2">
                <Power className="w-4 h-4" />
                <h2 className="text-lg font-semibold">State</h2>
              </div>
              <Row label="Power">{powerBadge(tv.powerStatus)}</Row>
              <Row label="On screen">{activeSourceText()}</Row>
              <Row label="CEC supported">{tv.cecSupported ? 'Yes' : 'No'}</Row>
            </CardContent>
          </Card>

          {/* Identity */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-2">
                <Info className="w-4 h-4" />
                <h2 className="text-lg font-semibold">Identity</h2>
              </div>
              <Row label="Make">{dash(tv.makeName || tv.make)}</Row>
              <Row label="Model">{dash(tv.model)}</Row>
              <Row label="Screen size">{sizeText()}</Row>
              <Row label="CEC vendor ID">{dash(tv.cecVendorId)}</Row>
              <Row label="CEC version">{dash(tv.cecVersion)}</Row>
              <Row label="CEC name (TV's own)">{dash(tv.cecOsdName)}</Row>
            </CardContent>
          </Card>

          {/* Capability */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-2">
                <Radio className="w-4 h-4" />
                <h2 className="text-lg font-semibold">Remote-control capability</h2>
              </div>
              <Row label="Ever forwarded a remote key">
                {tv.everReceivedRemoteKey ? (
                  <span className="text-green-700 font-medium">
                    Yes
                    {tv.firstRemoteKeyAtMs
                      ? ` (since ${new Date(tv.firstRemoteKeyAtMs).toLocaleDateString()})`
                      : ''}
                  </span>
                ) : (
                  <span className="text-muted-foreground">Not observed</span>
                )}
              </Row>
              <p className="text-xs text-muted-foreground mt-2">
                Whether this TV has ever sent the Hearth a D-pad / OK / Back key over CEC —
                evidence this model supports remote passthrough.
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

export default TvInfoViewer;
